import React, { useCallback, useContext, useEffect, useState } from "react";

import { faCircle, faHeadphones, faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCheckSquare } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";

import { AuthContext, type AuthPostMessage, type OAuthParams, promptGoogleAuth, promptOffice365Auth } from "~/lib/auth";
import { dispatchAnalyticsPage, dispatchAnalyticsTrack, getReferringEntities } from "~/lib/segment";
import DocumentTitle from "~/lib/title";
import { simpleHash } from "~/lib/utils";
import { Button } from "~/ui";

import { ReactComponent as CirclePattern } from "../../onboarding/css/circle-pattern.svg";
import { ReactComponent as SquarePattern } from "../../onboarding/css/todo-pattern.svg";
import type { AuthProps } from "../types";
import type { JoinWorkspaceResult } from "./SignupJoin";

import authStyles from "../css/Auth.module.css";
import styles from "../css/SignupConfirm.module.css";

interface ChoiceProps {
	personal?: boolean;
}

interface PointProps extends ChoiceProps {
	children: React.ReactNode;
}

const Point = ({ personal, children }: PointProps) => {
	return (
		<div className={styles.point}>
			<div className={personal ? styles.grey : styles.blue}>
				<FontAwesomeIcon icon={personal ? faCircle : faCheckSquare}></FontAwesomeIcon>
			</div>
			{children}
		</div>
	);
};

const Choice = ({ personal }: ChoiceProps) => {
	if (personal) {
		return (
			<div className={styles.choice}>
				<div className={styles.choiceHeader}>Fellow for Personal Use</div>
				<div className={styles.choiceBody}>
					<div className={styles.icon}>
						<FontAwesomeIcon icon={faHeadphones} />
					</div>
					<h6>Ideal for solopreneurs, consultants, and freelancers.</h6>
					<div className={styles.points}>
						<Point personal>
							<h6>Limited Collaboration</h6>
						</Point>
						<Point personal>
							<h6>Personal calendar sync</h6>
						</Point>
						<Point personal>
							<h6>Made for individuals</h6>
						</Point>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.choice}>
			<div className={styles.choiceHeader}>Fellow for Teams</div>
			<div className={styles.choiceBody}>
				<div className={styles.icon}>
					<FontAwesomeIcon icon={faUsers} />
				</div>
				<h6>For people who collaborate in meetings, teams, and more.</h6>
				<div className={styles.points}>
					<Point>
						<h6>Fully collaborative</h6>
					</Point>
					<Point>
						<h6>Business calendar sync</h6>
					</Point>
					<Point>
						<h6>Designed for teams</h6>
					</Point>
				</div>
			</div>
		</div>
	);
};

export default function SignupConfirm({ history, location }: AuthProps) {
	const authContext = useContext(AuthContext);
	const [loading, setLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const documentTitle = "Confirm | Signup";

	useEffect(() => {
		dispatchAnalyticsPage("Authentication", documentTitle);

		if (authContext.user?.email) {
			// window.heap?.identify(this.context.user.email);
			window.ga?.("set", "userId", simpleHash(authContext.user.email));
		}
	}, [authContext.user?.email]);

	const onWorkClick = useCallback(() => {
		/* eslint-disable camelcase */
		const { template } = window.INITIAL_STATE;

		let params: OAuthParams = { signup: "work" };
		if (template) params = { ...params, templateID: String(template.id) };
		params.referrer = JSON.stringify(getReferringEntities());

		const analyticsData = {
			feature: "Authentication",
			signup_email: authContext.user?.email,
			signup_email_type: "work",
			...getReferringEntities(),
		};
		/* eslint-enable camelcase */

		const authPrompt = location.state.signup?.provider === "google" ? promptGoogleAuth : promptOffice365Auth;
		authPrompt(params)
			.then(data => {
				dispatchAnalyticsTrack("Signup Confirm Step Completed", analyticsData);
				window.setTimeout(() => {
					if (data.signup?.redirect) {
						window.location.href = data.signup.redirect;
					} else if (data.signup?.next) {
						authContext.setAuthContext(data.signup);
						history.push(data.signup.next, data);
					} else if (data.next) {
						window.location.href = data.next;
					}
				}, 100);
			})
			.catch(({ message }: AuthPostMessage) => {
				dispatchAnalyticsTrack("Signup Confirm Step Failed", analyticsData);
				setErrorMessage(message);
			});
	}, [authContext, history, location.state.signup?.provider]);

	const onPersonalClick = useCallback(() => {
		setLoading(true);

		let data = new FormData();

		/* eslint-disable camelcase */
		const analyticsData = {
			account_id: null,
			feature: "Authentication",
			workspace_choice_type: "personal",
			...getReferringEntities(),
		};

		const { template } = window.INITIAL_STATE;
		if (template) {
			data.append("template_id", String(template.id));
		}
		/* eslint-enable camelcase */

		fetch("/auth/save/join/", {
			method: "POST",
			body: data,
			headers: {
				"x-client-session-id": window.CLIENT_SESSION_ID,
				"x-csrftoken": window.CSRF_TOKEN,
				"x-requested-with": "XMLHttpRequest",
			},
		})
			.then((res: Response) => {
				return res.json();
			})
			.then((data: JoinWorkspaceResult) => {
				dispatchAnalyticsTrack("Signup Confirm Step Completed", analyticsData);
				/* eslint-disable camelcase */
				dispatchAnalyticsTrack("Signed Up", {
					is_account_created: true,
					...analyticsData,
				});
				/* eslint-enable camelcase */

				window.setTimeout(() => {
					setLoading(false);
					if (data.success) {
						if (data.signup.redirect) {
							window.location.href = data.signup.redirect;
						} else if (data.signup.next) {
							authContext.setAuthContext(data.signup);
							history.push(data.signup.next, location.state);
						}
					} else if (data.errorMessage) {
						dispatchAnalyticsTrack("Signup Confirm Step Failed", analyticsData);
						setErrorMessage(data.errorMessage);
					}
				}, 100);
			})
			.catch(err => {
				Sentry.captureException(err);
				dispatchAnalyticsTrack("Signup Join Step Failed", analyticsData);
				setLoading(false);
				setErrorMessage(`An error occurred: ${err}`);
			});
	}, [authContext, history, location.state]);

	return (
		<div className={styles.main}>
			<DocumentTitle title={documentTitle}></DocumentTitle>
			<div className={styles.container}>
				<div className={styles.header}>
					{errorMessage && (
						<div data-test-id="auth-error-message" className={authStyles.authError}>
							{errorMessage}
						</div>
					)}
				</div>
				<div className={styles.body}>
					<SquarePattern className={styles.squarePattern}></SquarePattern>
					<CirclePattern className={styles.circlePattern}></CirclePattern>
					<div className={styles.intro}>
						<h2>Let&apos;s set your email preference</h2>
						<h5>
							You&apos;ve signed up with <span className={styles.emph}>{authContext.user?.email}</span> which looks like
							a personal email.
						</h5>
					</div>
					<div className={styles.choices}>
						<div className={styles.choiceContainer}>
							<Choice personal></Choice>
							<div className={styles.choiceButtonContainer}>
								<Button size="wide" onClick={onPersonalClick} className={styles.choiceButton} disabled={loading}>
									{loading ? (
										<span>
											Creating your workspace...
											<FontAwesomeIcon spin icon={faSpinnerThird}></FontAwesomeIcon>
										</span>
									) : (
										"Continue with personal email"
									)}
								</Button>
							</div>
						</div>
						<div className={styles.choiceContainer}>
							<Choice></Choice>
							<div className={styles.choiceButtonContainer}>
								<Button
									size="wide"
									onClick={onWorkClick}
									variant="primary"
									className={styles.choiceButton}
									disabled={loading}
								>
									Continue with work email
								</Button>
								<div>Recommended</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
